<template>
  <form class="form pt-3 container-fluid"
    v-on:submit.prevent="validateFields">
    <h5 v-if="formObject.title" class="form-title">{{ formObject.title }}</h5>
    <div class="description" v-if="formObject.description" v-html="sanitizedDescription"></div>
    <QuestionGroup v-if="Object.keys(formGroups).length"
      v-for="(group, index) in formGroups"
      v-bind:key="group.key"
      v-bind:groupIndex="index"
      v-bind:groupId="group.groupId"
      v-bind:formFields="group.fields"
      v-bind:formSubmitFields="formSubmitFields"/>
    <div class="row align-items-center sticky-button-container bg-white py-3 border-top mx-0">
      <div class="col pl-0">
        <span v-if="getCurrentForms().length && !fullScreen" class="text-muted">
          Step {{ currentStep }} of {{ getCurrentForms().length }}</span>
      </div>
      <div class="col-auto pr-0">
        <CancelButton location="reporting-form" v-if="!fullScreen">
          <CmsText cms-key="reporting_form:cancel_button_text" :default-value="defaultTexts.CANCEL_BUTTON"></CmsText>
        </CancelButton>
        <SubmitButton v-bind:inSubmission="inSubmission" :class="{ fullscreen: fullScreen }">
          <CmsText v-if="!isLastForm()" cms-key="reporting_form:continue_button_text" :default-value="defaultTexts.CONTINUE_BUTTON"></CmsText>
          <CmsText v-if="isLastForm()" cms-key="reporting_form:finish_button_text" :default-value="defaultTexts.FINISH_BUTTON"></CmsText>
        </SubmitButton>
      </div>
    </div>
    <no-panel-upgrade-modal></no-panel-upgrade-modal>
    <water-heater-learn-more-modal></water-heater-learn-more-modal>
  </form>
</template>

<script>
import FormMixin from '@/mixins/FormMixin';
import QuestionGroup from '@/components/QuestionGroup.vue';
import SubmitButton from '@/components/SubmitButton.vue';
import CancelButton from '@/components/CancelButton.vue';
import CmsText from '@/components/CmsText.vue';
import constants from '@/constants';
import { mapState, mapGetters } from 'vuex';
import NoPanelUpgradeModal from '../../modals/NoPanelUpgrade.vue';
import WaterHeaterLearnMoreModal from '../../modals/WaterHeaterLearnMore.vue';

/**
 * Reporting Form
 *
 * This form is used to gather additional data related to the users application.
 */
export default {
  name: 'ReportingForm',
  components: {
    QuestionGroup,
    SubmitButton,
    CancelButton,
    CmsText,
    NoPanelUpgradeModal,
    WaterHeaterLearnMoreModal,
  },
  mixins: [
    FormMixin,
  ],
  data() {
    return {
      formGroups: [],
    };
  },
  watch: {
    $route() {
      // Ensures the currentFormIndex is correct,
      // particularly when the browser back button is used.
      this.$store.commit('forms/setCurrentFormIndex', this.$route.params.index);

      // Vue doesn't allow you to route to a second instance of a view,
      // so we need to update the view with the correct form and submission status.
      this.inSubmission = false;
      this.createForm();
    },
    forms() {
      this.createForm();
    },
    formFields() {
      this.trackCurrentFormEvent(this.segment.constants.events.OPENED);
    },
  },
  computed: {
    ...mapState('forms', ['forms']),
    ...mapState('customerData', ['validationBypassCode']),
    // Gets the current step of the verification process
    currentStep() {
      return Number(this.getCurrentFormIndex()) + 1;
    },
    defaultTexts() {
      return constants.defaultTexts;
    },
  },
  methods: {
    ...mapGetters('forms', ['getCurrentFormObject', 'getCurrentFormIndex', 'getCurrentForms']),
    // Validate the fields of the current form, and if they are successful,
    // submit the form's data to the backend.
    validateFields() {
      this.$validator.validate().then((result) => {
        if (result) {
          this.submitFormData(this.formObject.type);
        }
      });
    },
    /**
     * Sorts question groups by group id.
     * @param groupsByProductId
     */
    sortGroups(groupsByProductId) {
      this.formGroups = [];
      Object.keys(groupsByProductId).forEach((groupId) => {
        this.formGroups.push({
          key: `${this.currentStep}-${groupId}`,
          groupId,
          fields: groupsByProductId[groupId],
        });
      });
      this.formGroups.sort((a, b) => a.productId - b.productId);
    },
    /**
     * Groups fields by category when a product id isn't specified.
     * @param fields
     */
    groupFieldsByCategory(fields) {
      const groupsByCategoryId = {};
      fields.forEach((field) => {
        if (field.category_id in groupsByCategoryId) {
          groupsByCategoryId[field.category_id].push(field);
        } else {
          groupsByCategoryId[field.category_id] = [field];
        }
      });
      return groupsByCategoryId;
    },
    // Groups fields by product id and index
    groupFieldsByProduct() {
      const groupsByProductId = {};
      const nullProductFields = [];
      this.formFields.forEach((field) => {
        const groupId = `${field.product_id}__${field.product_index}`;
        if (field.product_id === null) {
          nullProductFields.push(field);
        } else if (groupId in groupsByProductId) {
          groupsByProductId[groupId].push(field);
        } else {
          groupsByProductId[groupId] = [field];
        }
      });
      const categoryGroups = this.groupFieldsByCategory(nullProductFields);
      this.sortGroups(Object.assign(groupsByProductId, categoryGroups));
    },
    // Gets form to be created and groups form fields by product
    createForm() {
      if (this.forms.length > 0) {
        this.initializeForm();
        this.groupFieldsByProduct();
      }
    },
  },
  created() {
    this.createForm();
  },
};
</script>
